import React from "react";
import {
  FiArrowRight,
  FiChevronRight,
  FiBookOpen,
  FiSearch,
  FiMessageSquare,
  FiMail,
  FiRepeat,
  FiHome,
  FiShoppingBag,
  FiBriefcase,
  FiHeadphones,
  FiZap,
  FiTarget,
  FiTrendingUp,
  FiMessageCircle,
  FiUsers,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LandingHeader from "../components/landing/LandingHeader";
import LandingFooter from "../components/landing/LandingFooter";
import ProductSeoContent from "../components/seo/ProductSeoContent";
import productDemo from "../static/images/product-demo.png";
import productDemoJapanese from "../static/images/image 41.png";
import productChat from "../static/images/product-chat.png";
import productHr from "../static/images/product-hr.png";
import productKnowledge from "../static/images/product-knowledge.png";
import productIntegrations from "../static/images/product-integrations.png";
import productIndustry from "../static/images/product-industry.png";

const Product = () => {
  const translation = useSelector((state) => state.translation);
  const navigate = useNavigate();
  const isJapanese = (localStorage.getItem("lang") || "en") === "ja";

  const goToExperienceSection = () => {
    navigate("/#demo");
  };
  const sections = [
    {
      label: translation.product_section1_label || "INTERACTIVE PRODUCT DEMOS",
      id: "interactive-demos",
      image: isJapanese ? productDemoJapanese : productDemo,
      title:
        translation.product_section1_title ||
        "Show Your Product Without Meetings - Using Interactive Demos",
      text:
        translation.product_section1_text ||
        "esyncing lets you create interactive product demos that users can experience on their own - no calls, no long explanations, just hands-on learning.",
      groups: [
        {
          icon: <FiZap />,
          title:
            translation.product_section1_group1_title ||
            "Create interactive product walkthroughs",
          items: [
            translation.product_section1_group1_item1 || "Guide users step by step",
            translation.product_section1_group1_item2 || "Explain features without live calls",
          ],
        },
        {
          icon: <FiTarget />,
          title:
            translation.product_section1_group2_title ||
            "Let users experience the product instantly",
          items: [
            translation.product_section1_group2_item1 || "Allow users to explore at their own pace",
            translation.product_section1_group2_item2 || "Reduce time to product understanding",
          ],
        },
        {
          icon: <FiTrendingUp />,
          title:
            translation.product_section1_group3_title ||
            "Track engagement and understanding",
          items: [
            translation.product_section1_group3_item1 || "See how users interact with demos",
            translation.product_section1_group3_item2 || "Improve demos based on user behavior",
          ],
        },
      ],
      cta: translation.product_section1_cta || "Try Interactive Demo",
      link: translation.product_section1_link || "How Interactive Demos Work",
      caption:
        translation.product_section1_caption ||
        "This is a preview. Start the demo to experience the full product flow.",
      flip: true,
      hero: true,
    },
    {
      label: translation.product_section2_label || "AI-POWERED CHAT",
      id: "ai-chat",
      image: productChat,
      title:
        translation.product_section2_title ||
        "Let Teams and Customers Communicate in Real Time",
      text:
        translation.product_section2_text ||
        "esyncing connects teams and customers through AI-powered chat. Handle support, coordinate work, and answer questions - all in real time, with an AI assistant that helps reduce manual effort.",
      groups: [
        {
          icon: <FiMessageCircle />,
          title:
            translation.product_section2_group1_title ||
            "1-to-1 customer & team chat",
          items: [
            translation.product_section2_group1_item1 || "Respond to customers instantly",
            translation.product_section2_group1_item2 || "Maintain consistent responses at scale",
          ],
        },
        {
          icon: <FiUsers />,
          title:
            translation.product_section2_group2_title ||
            "Group chats for team collaboration",
          items: [
            translation.product_section2_group2_item1 || "Coordinate discussions in shared conversations",
            translation.product_section2_group2_item2 || "Keep teams aligned in real time",
          ],
        },
        {
          icon: <FiZap />,
          title:
            translation.product_section2_group3_title ||
            "AI assistance inside conversations",
          items: [
            translation.product_section2_group3_item1 || "Reduce workload with AI-powered support",
            translation.product_section2_group3_item2 || "Help answer questions and fetch information instantly",
          ],
        },
      ],
      cta: translation.product_section2_cta || "Try Demo",
      link: translation.product_section2_link || "How AI Chat Works",
      caption:
        translation.product_section2_caption ||
        "This is a preview. Start the demo to experience real-time chat.",
      flip: false,
      dark: true,
    },
    {
      label: translation.product_section3_label || "VIRTUAL HR ASSISTANT",
      id: "virtual-hr",
      image: productHr,
      title:
        translation.product_section3_title || "Let Employees Get HR Answers Instantly",
      text:
        translation.product_section3_text ||
        "esyncing's Virtual HR Assistant helps employees find HR information, understand policies, and get answers—all using your company's actual data and guidelines.",
      groups: [
        {
          icon: <FiUsers />,
          title:
            translation.product_section3_group1_title ||
            "Employee self-service HR support",
          items: [
            translation.product_section3_group1_item1 || "Answer employee questions instantly",
            translation.product_section3_group1_item2 || "Provide accurate, policy-based responses",
          ],
        },
        {
          icon: <FiTarget />,
          title:
            translation.product_section3_group2_title ||
            "HR policies & company rules",
          items: [
            translation.product_section3_group2_item1 || "Access HR documents and policies easily",
            translation.product_section3_group2_item2 || "Get clear answers from internal HR data",
          ],
        },
        {
          icon: <FiZap />,
          title:
            translation.product_section3_group3_title ||
            "AI-trained on your company data",
          items: [
            translation.product_section3_group3_item1 || "Use company rules, files, and guidelines",
            translation.product_section3_group3_item2 || "Reduce repetitive HR queries",
          ],
        },
      ],
      cta: translation.product_section3_cta || "Try Demo",
      link: translation.product_section3_link || "How Virtual HR Works",
      caption:
        translation.product_section3_caption ||
        "This is a preview. Start the demo to ask HR questions.",
      flip: true,
    },
    {
      label: translation.product_section4_label || "COMPANY KNOWLEDGE HUB",
      id: "knowledge-hub",
      image: productKnowledge,
      title:
        translation.product_section4_title || "Give Teams One Place to Find Answers",
      text:
        translation.product_section4_text ||
        "esyncing's Knowledge Hub centralizes company information and uses AI to help employees find what they need - instantly, without asking around.",
      groups: [
        {
          icon: <FiBookOpen />,
          title:
            translation.product_section4_group1_title ||
            "Centralized company knowledge",
          items: [
            translation.product_section4_group1_item1 || "Store policies, guides, and internal documents",
            translation.product_section4_group1_item2 || "Access information from one source of truth",
          ],
        },
        {
          icon: <FiSearch />,
          title:
            translation.product_section4_group2_title ||
            "AI-powered knowledge search",
          items: [
            translation.product_section4_group2_item1 || "Get instant answers from company content",
            translation.product_section4_group2_item2 || "Reduce time spent searching for information",
          ],
        },
        {
          icon: <FiUsers />,
          title:
            translation.product_section4_group3_title ||
            "Self-service for teams",
          items: [
            translation.product_section4_group3_item1 || "Enable employees to find answers on their own",
            translation.product_section4_group3_item2 || "Reduce repeated questions to teams",
          ],
        },
      ],
      cta: translation.product_section4_cta || "Try Demo",
      link: translation.product_section4_link || "How Knowledge Hub Works",
      caption:
        translation.product_section4_caption ||
        "This is a preview. Start the demo to search company knowledge.",
      flip: false,
    },
    {
      label: translation.product_section5_label || "INTEGRATIONS",
      id: "integrations",
      image: productIntegrations,
      title:
        translation.product_section5_title ||
        "Connect Your Tools and Let AI Work Across Platforms",
      text:
        translation.product_section5_text ||
        "esyncing connects with your existing tools so teams can communicate, automate, and use AI without switching platforms.",
      groups: [
        {
          icon: <FiMessageSquare />,
          title:
            translation.product_section5_group1_title ||
            "Messaging & communication tools",
          items: [
            translation.product_section5_group1_item1 || "Connect WhatsApp conversations",
            translation.product_section5_group1_item2 || "Sync Slack messages and teams",
          ],
        },
        {
          icon: <FiMail />,
          title:
            translation.product_section5_group2_title ||
            "Google Workspace integrations",
          items: [
            translation.product_section5_group2_item1 || "Access and respond to Gmail conversations",
            translation.product_section5_group2_item2 || "Use Google Docs and Drive as knowledge sources",
          ],
        },
        {
          icon: <FiRepeat />,
          title:
            translation.product_section5_group3_title ||
            "Connected workflows",
          items: [
            translation.product_section5_group3_item1 || "Share data across connected tools",
            translation.product_section5_group3_item2 || "Trigger AI actions from integrations",
          ],
        },
      ],
      cta: translation.product_section5_cta || "Try Demo",
      link: translation.product_section5_link || "Industry Examples",
      caption:
        translation.product_section5_caption ||
        "This is a preview. Connect your tools to enable integrations.",
      flip: true,
    },
    {
      label: translation.product_section6_label || "INDUSTRY USE CASES",
      id: "industry",
      image: productIndustry,
      title:
        translation.product_section6_title || "One Platform for Every Industry",
      text:
        translation.product_section6_text ||
        "esyncing adapts to your industry's needs-whether you manage properties, sell products, support teams, or serve customers.",
      groups: [
        {
          icon: <FiHome />,
          title: translation.product_section6_group1_title || "Real Estate",
          items: [
            translation.product_section6_group1_item1 || "Handle tenant and client conversations",
            translation.product_section6_group1_item2 || "Manage property-related information",
            translation.product_section6_group1_item3 || "Automate responses to common inquiries",
          ],
        },
        {
          icon: <FiShoppingBag />,
          title: translation.product_section6_group2_title || "E-commerce",
          items: [
            translation.product_section6_group2_item1 || "Support customers in real time",
            translation.product_section6_group2_item2 || "Answer order and delivery questions",
            translation.product_section6_group2_item3 || "Reduce support workload with AI",
          ],
        },
        {
          icon: <FiBriefcase />,
          title: translation.product_section6_group3_title || "Corporate & Enterprises",
          items: [
            translation.product_section6_group3_item1 || "Enable internal team communication",
            translation.product_section6_group3_item2 || "Support employees with HR and policy queries",
            translation.product_section6_group3_item3 || "Centralize company knowledge",
          ],
        },
        {
          icon: <FiHeadphones />,
          title: translation.product_section6_group4_title || "Customer Support Teams",
          items: [
            translation.product_section6_group4_item1 || "Respond to customer questions instantly",
            translation.product_section6_group4_item2 || "Handle repetitive queries automatically",
            translation.product_section6_group4_item3 || "Improve response efficiency",
          ],
        },
      ],
      cta: translation.product_section6_cta || "Try Demo",
      link: translation.product_section6_link || "All Use Cases",
      caption:
        translation.product_section6_caption ||
        "This is a preview. Explore use cases across industries.",
      flip: false,
    },
  ];

  return (
    <div className="product-page">
      <ProductSeoContent />
      <LandingHeader />
      <main className="product-main">
        {sections.map((section, idx) => (
          <section
            key={section.title}
            id={section.id}
            className={`product-section ${idx === 0 ? "product-first" : ""} ${
              section.flip ? "flip" : ""
            } ${section.hero ? "product-hero-section" : ""} ${
              section.dark ? "product-dark" : ""
            }`}
          >
            <div className="product-card">
              <div className="product-container">
                <div className="product-content">
                  <div className="product-label">{section.label}</div>
                  <h2>{section.title}</h2>
                  <p className="product-text">{section.text}</p>
                  {section.groups && (
                    <div className="product-groups">
                      {section.groups.map((group) => (
                        <div className="product-group" key={group.title}>
                          <div className="group-title">
                            <span className="group-icon">{group.icon}</span>
                            <span>{group.title}</span>
                          </div>
                          <ul>
                            {group.items.map((item) => (
                              <li key={item}>
                                <FiChevronRight />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  {section.points && (
                    <ul className="product-points">
                      {section.points.map((item) => (
                        <li key={item}>
                          <FiChevronRight />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="product-actions">
                    <button
                      className="landing-btn landing-btn-primary"
                      onClick={goToExperienceSection}
                    >
                      {section.cta}
                    </button>
                    <button
                      className="landing-btn landing-btn-link"
                      onClick={goToExperienceSection}
                    >
                      {section.link} <FiArrowRight />
                    </button>
                  </div>
                </div>
                <div className="product-media">
                  <img
                    className="product-media-image"
                    src={section.image}
                    alt={section.title}
                  />
                  {section.caption && (
                    <div className="product-media-caption">{section.caption}</div>
                  )}
                </div>
              </div>
            </div>
          </section>
        ))}
        <section className="product-cta">
          <div className="product-container">
            <div>
              <h3
                dangerouslySetInnerHTML={{
                  __html:
                    translation.product_cta_title ||
                    "Bring all your conversations,<br />knowledge, and AI workflows<br />into one platform",
                }}
              />
              <p>
                {translation.product_cta_text ||
                  "Experience how esyncing connects teams, customers, and AI - all in one place."}
              </p>
              <div className="product-actions center">
                <button
                  className="landing-btn landing-btn-primary"
                  onClick={goToExperienceSection}
                >
                  {translation.product_cta_primary || "Try Demo"}
                </button>
                <button
                  className="landing-btn landing-btn-ghost"
                  onClick={goToExperienceSection}
                >
                  {translation.get_started || "Get Started"}
                </button>
              </div>
            </div>
          </div>
        </section>
</main>
      <LandingFooter />
    </div>
  );
};

export default Product;
